import { Navigate, createBrowserRouter } from "react-router";
import type { RouteObject } from "react-router";
import { AppShell } from "./AppShell";
import { RequireAuth, RequireConsent, RequirePasswordChange, RequireRole } from "./guards";
import { RouteErrorElement } from "./ErrorBoundary";
import { NotFoundPage } from "./NotFoundPage";
import { homeFor, landingRole } from "./roles";
import {
  ConsentPage,
  ForgotPasswordPage,
  InviteAcceptPage,
  InviteCodePage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  SetupPage,
} from "../features/auth";
import { useAuth } from "../features/auth/useAuth";
import { passwordRoute, routes as accountRoutes } from "../features/account";
import { routes as studentRoutes, unguardedRoutes as studentOwnRoutes } from "../features/student";
import { routes as annotatorRoutes } from "../features/annotator";
import { routes as adjudicatorRoutes } from "../features/adjudicator";
import { routes as adminRoutes } from "../features/admin";

/** `/` sends each user to the home of their primary role. */
function IndexRedirect() {
  const { me, isLoading } = useAuth();
  if (isLoading) return <p className="xz-container gz-muted">正在加载…</p>;
  if (!me) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(landingRole(me.user.roles))} replace />;
}

export const routes: RouteObject[] = [
  { path: "/login", element: <LoginPage />, errorElement: <RouteErrorElement /> },
  { path: "/register", element: <RegisterPage />, errorElement: <RouteErrorElement /> },
  { path: "/setup", element: <SetupPage />, errorElement: <RouteErrorElement /> },
  { path: "/invite", element: <InviteCodePage />, errorElement: <RouteErrorElement /> },
  { path: "/invite/:token", element: <InviteAcceptPage />, errorElement: <RouteErrorElement /> },
  { path: "/reset/:token", element: <ResetPasswordPage />, errorElement: <RouteErrorElement /> },
  // ACCOUNTS.md §4 忘记密码自助 — public; the response is always generic.
  { path: "/forgot", element: <ForgotPasswordPage />, errorElement: <RouteErrorElement /> },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorElement />,
    children: [
      {
        element: <AppShell />,
        children: [
          // ACCOUNTS.md §3 — always reachable, even with a temporary password.
          passwordRoute,
          {
            element: <RequirePasswordChange />,
            children: [
              { index: true, element: <IndexRedirect /> },
              { path: "/consent", element: <ConsentPage /> },
              ...accountRoutes,

              // 学生端
              {
                element: <RequireRole roles={["student"]} />,
                children: [
                  ...studentOwnRoutes,
                  { element: <RequireConsent />, children: studentRoutes },
                ],
              },

              // 专家端 / 仲裁端 / 管理端 — filled in by the other agents.
              {
                element: <RequireRole roles={["annotator"]} />,
                children: annotatorRoutes,
              },
              {
                element: <RequireRole roles={["adjudicator", "course_team"]} />,
                children: adjudicatorRoutes,
              },
              {
                // 班级教师 sees the class-level screens; the server enforces
                // the finer-grained matrix (AUTHZ.md).
                element: <RequireRole roles={["course_team", "admin", "teacher"]} />,
                children: adminRoutes,
              },

              { path: "*", element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
];

export function createRouter() {
  return createBrowserRouter(routes);
}
